import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
	ExternalLink,
	FlaskConical,
	ListChecks,
	MessageSquareText,
	Sparkles,
} from 'lucide-react';
import { PageTitle } from '@/components/ui';
import { EditForm } from '@/pages/admin/forms/EditForm';
import { useGetForms } from '@/services/queries/form';
import { useCreateForm, usePatchForm } from '@/services/mutations/form';

/**
 * Admin › Match: gestão das perguntas, alternativas e ordem do questionário /match.
 * O formulário é um `form` de tipo MATCH; o editor é o mesmo dos formulários (embutido),
 * com setas de reordenação. Só um formulário MATCH fica ativo por vez.
 */
const MatchAdminPage = () => {
	const { data, isLoading } = useGetForms({
		type: 'MATCH',
		page: 1,
		limit: 20,
	});
	const { mutate: createForm, isPending: isCreating } = useCreateForm();
	const { mutate: patchForm, isPending: isPatching } = usePatchForm();

	const matchForms = data?.data ?? [];
	const matchForm = useMemo(
		() =>
			[...matchForms].sort((a, b) => {
				if (a.active !== b.active) return a.active ? -1 : 1;
				return b.created_at.localeCompare(a.created_at);
			})[0],
		[matchForms],
	);

	const handleCreate = () => {
		createForm({
			type: 'MATCH',
			title: 'Match Plumaa',
			active: true,
			application_mode: 'LIFETIME_SINGLE',
		});
	};

	const toggleActive = () => {
		if (!matchForm) return;
		patchForm({ id: matchForm.id, body: { active: !matchForm.active } });
	};

	const statCard =
		'flex items-center gap-3 rounded-2xl border border-stone-100 bg-white px-4 py-3 shadow-sm';

	return (
		<section className="space-y-6 px-8 py-8">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="space-y-1">
					<div className="flex items-center gap-3">
						<span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-pink text-white">
							<Sparkles className="h-5 w-5" />
						</span>
						<PageTitle text="Match" />
					</div>
					<p className="text-sm text-stone-400">
						Perguntas, alternativas e ordem do questionário público
						em{' '}
						<code className="rounded bg-stone-100 px-1.5 py-0.5 text-xs text-stone-600">
							/match
						</code>
						. Cada usuário responde uma única vez; ao final vê 3
						profissionais.
					</p>
				</div>

				<div className="flex flex-wrap items-center gap-2">
					<Link
						to="/admin/match/simulador"
						className="inline-flex items-center gap-2 rounded-full bg-brand-pink px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-pink/90"
					>
						<FlaskConical className="h-3.5 w-3.5" />
						Simulador
					</Link>
					<a
						href="/match"
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900 hover:text-stone-900"
					>
						<ExternalLink className="h-3.5 w-3.5" />
						Abrir /match
					</a>
					{matchForm ? (
						<button
							type="button"
							onClick={toggleActive}
							disabled={isPatching}
							className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors disabled:opacity-60 ${
								matchForm.active
									? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
									: 'bg-stone-900 text-white hover:bg-stone-800'
							}`}
						>
							<span
								className={`h-2 w-2 rounded-full ${
									matchForm.active
										? 'bg-emerald-500'
										: 'bg-stone-400'
								}`}
							/>
							{matchForm.active
								? 'Ativo no site'
								: 'Desativado (ativar)'}
						</button>
					) : null}
				</div>
			</div>

			{isLoading ? (
				<p className="text-sm text-stone-400">
					Carregando formulário do match...
				</p>
			) : !matchForm ? (
				<div className="rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 p-10 text-center">
					<h3 className="text-base font-black text-stone-900">
						Nenhum formulário de match cadastrado
					</h3>
					<p className="mx-auto mt-2 max-w-md text-sm text-stone-400">
						Crie o formulário para começar a cadastrar as perguntas.
						Enquanto não houver um formulário ativo, a página /match
						mostra um aviso de indisponibilidade.
					</p>
					<button
						type="button"
						onClick={handleCreate}
						disabled={isCreating}
						className="mt-6 inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-stone-800 disabled:opacity-60"
					>
						<Sparkles className="h-4 w-4" />
						{isCreating
							? 'Criando...'
							: 'Criar formulário do match'}
					</button>
				</div>
			) : (
				<>
					<div className="grid gap-3 sm:grid-cols-3">
						<div className={statCard}>
							<ListChecks className="h-4 w-4 text-stone-400" />
							<div>
								<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
									Perguntas
								</p>
								<p className="text-base font-black text-stone-900">
									{matchForm.questions_count}
								</p>
							</div>
						</div>
						<div className={statCard}>
							<MessageSquareText className="h-4 w-4 text-stone-400" />
							<div>
								<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
									Respostas
								</p>
								<p className="text-base font-black text-stone-900">
									{matchForm.answered}
								</p>
							</div>
						</div>
						<div className={statCard}>
							<Sparkles className="h-4 w-4 text-stone-400" />
							<div>
								<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
									Ranking
								</p>
								<p className="text-xs font-bold text-stone-700">
									Ordem de exibição dos profissionais (por
									enquanto)
								</p>
							</div>
						</div>
					</div>

					<div className="rounded-2xl border border-amber-100 bg-amber-50/60 px-4 py-3 text-xs font-medium text-amber-900">
						Use as setas ao lado de cada pergunta para mudar a ordem
						em que aparecem. Perguntas condicionais (exibidas
						conforme outra resposta) têm a referência atualizada
						automaticamente. Chaves das alternativas devem ter 1
						letra (A, B, C...).
					</div>

					<EditForm formIdOverride={matchForm.id} embedded />
				</>
			)}
		</section>
	);
};

export { MatchAdminPage };
